import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, driverApi } from '../lib/api.ts';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { BRAND } from '../lib/brand.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import type { Driver } from '../lib/types.ts';

type DriverWithCompany = Driver & {
  company: { id: string; company_name: string; contact_name: string } | null;
};

export function DriverShell() {
  const { session, loading, signOut } = useCompanyAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['driver-me'],
    queryFn: () => driverApi.get<{ driver: DriverWithCompany }>('/api/driver/me'),
    enabled: !!session,
  });

  useEffect(() => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      void signOut();
      navigate('/c/expired', { replace: true });
    }
  }, [error, signOut, navigate]);

  if (loading) return <CenteredSpinner />;
  if (!session) {
    return <Navigate to="/c/expired" state={{ from: location }} replace />;
  }
  if (isLoading) return <CenteredSpinner />;
  const driver = data?.driver;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b bg-white" style={{ borderColor: '#E2E8F0' }}>
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
              style={{ background: 'var(--brand-primary)' }}
            >
              <img src={BRAND.logoUrl} alt={BRAND.logoAlt} className="h-5 w-auto" />
            </div>
            <div>
              <div
                className="text-[15px] font-bold leading-tight tracking-tight"
                style={{ color: 'var(--brand-primary)' }}
              >
                Driver onboarding
              </div>
              {driver && (
                <div className="text-xs text-slate-500">
                  {driver.name}
                  {driver.company?.company_name ? ` · ${driver.company.company_name}` : ''}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {driver && (
              <div className="hidden sm:block">
                <CompanyStatusBadge status={driver.status} audience="vendor" />
              </div>
            )}
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>
        {driver && (
          <div className="mx-auto max-w-5xl px-6 pb-4">
            <ProgressBar value={driver.progress} label="Progress" />
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
